import {
    HttpErrorResponse,
    HttpHandler,
    HttpInterceptor,
    HttpRequest
} from "@angular/common/http";
import { catchError } from "rxjs/operators";
import { throwError, of } from "rxjs";
import { Injectable } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { ErrorComponent } from "./error/error.component";
import { AuthService } from "./auth/auth.service";
import { DialogService } from "./dialog/dialog.service";
// import { TranslateService } from '@ngx-translate/core';
import * as Sentry from "@sentry/angular";
import { environment } from "src/environments/environment";

/** Minimum ms between logout() calls for auth failure – prevents burst of 401s from causing repeated logout/navigate. */
const AUTH_LOGOUT_COOLDOWN_MS = 3000;

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
    private lastAuthLogoutTime = 0;

    constructor(
        private dialog: MatDialog,
        private authService: AuthService,
        private dialogService: DialogService,
        // private translationService: TranslateService,
    ) { }

    intercept(req: HttpRequest<any>, next: HttpHandler) {
        return next.handle(req).pipe(
            catchError((error: HttpErrorResponse) => {
                if ((error.status === 0 && error.statusText === 'Unknown Error')
                    || error.message === "GET_QUEUE_STATUS_FAILED") {
                    // console.error('Ignored error:', error);
                    return of(null); // Ignore the error and return null
                }

                if (req.url.startsWith(environment.apiUrl + '/r/')) {
                    // receipt link (/r/:token page) — the page shows "not found" itself
                    return throwError(error);
                }

                let errorMessage: string = error.error?.message || "unknown_error";
                if (errorMessage === "unknown_error") {
                    Sentry.captureException(error);
                    // עבור בקשות תשלום – שגיאה ללא message ברור כנראה בעיית חיוב
                    if (
                        req.url.includes('/chat/price-quote/pay') ||
                        req.url.includes('/orders/payemptyfixproductsorder')
                    ) {
                        errorMessage = 'PAYMENT_ERROR';
                    }
                }

                if (errorMessage === 'AUTH_FAILED_TOKEN_INCORECT_LOGOUT') {
                    const cooldownPassed = (Date.now() - this.lastAuthLogoutTime) >= AUTH_LOGOUT_COOLDOWN_MS;
                    const shouldClearAuth = this.authService.getToken() != null || this.authService.getIsAuth();
                    if (cooldownPassed && shouldClearAuth) {
                        this.lastAuthLogoutTime = Date.now();
                        this.authService.logout();
                    }
                } else if (error.status === 403 && (errorMessage === 'UNAUTHORIZED' || errorMessage === 'Unauthorized') && req.url.includes('/chat/user/')) {
                    // 403 על נתיבי צ'אט משתמש – הקומפוננטה (ChatComponent) תציג הודעה ותניווט; לא לפתוח דיאלוג שגיאה גנרי
                    return throwError(error);
                } else if (
                    (errorMessage === 'FETCHING_ORDER_FAILED' || errorMessage === 'SYSTEM_CHAT_FETCH_FAILED') &&
                    req.url.includes('/chat/')
                ) {
                    // צ'אט: לא להציג שגיאה – הקומפוננטה תבצע retry אוטומטי
                } else if (req.url.includes('/user/bmph-users')) {
                    // PH: כשל בשרת ברשימת bmph – לא דיאלוג FETCHING_USER_FAILED; השירות מחזיר רשימה ריקה
                    console.warn('bmph-users request failed:', error.status, error.error);
                    return throwError(error);
                } else if (errorMessage === 'unknown_error') {
                    // For unknown_error, only log to console - don't disconnect user or show error dialog
                    // This prevents redirect loops when there are network/CORS issues (status 0 handled above)
                    console.warn('Unknown error occurred:', error);
                    // הצגת דיאלוג רק עבור תשלום PH (כבר הוחלף ל-PAYMENT_ERROR למעלה)
                    // לא מגיע לכאן – unknown_error על pay הופך ל-PAYMENT_ERROR
                } else if (
                    errorMessage === 'PAYMENT_ERROR' &&
                    req.url.includes('/chat/price-quote/pay') &&
                    (error.error?.message === 'PAYMENT_ERROR' || error.error?.error === 'PAYMENT_ERROR')
                ) {
                    // השרת החזיר במפורש PAYMENT_ERROR – הקומפוננטה תפתח חלונית סיכום
                } else if (req.url.includes('/user/otp/') || req.url.includes('/user/phone/')) {
                    // התחברות בטלפון / אימות טלפון — הדיאלוג מציג את השגיאה בתוכו
                } else if (req.url.includes('/wall/contact')) {
                    // דיאלוג הפנייה מציג snackbar משלו
                } else if (errorMessage === 'BRANCH_IS_CLOSED') {
                    // Open the dedicated close branch dialog instead of generic error
                    const service = localStorage.getItem('printingService') || '';
                    const branch = localStorage.getItem('branch') || '';
                    const closeMsg = error.error?.close_msg || '';
                    const isPicsPrinter = error.error?.isPicsPrinter || false;
                    const branchId = error.error?.branchId || null;
                    
                    this.dialogService.onOpenCloseBranchDialog(
                        service,
                        branch,
                        closeMsg,
                        isPicsPrinter,
                        branchId
                    );
                } else {
                    // Pass branchName only for NO_PRINTER_FOUND_WITH_THIS_CODE error
                    const branchName =
                        errorMessage === 'NO_PRINTER_FOUND_WITH_THIS_CODE'
                            ? (
                                error.error?.branchName ||
                                error.error?.branch ||
                                error.error?.serial_name ||
                                error.error?.printerSerialName ||
                                error.error?.branchSerialName ||
                                localStorage.getItem('branch') ||
                                null
                              )
                            : null;
                    const rawDetails = error.error?.details;
                    const details =
                        typeof rawDetails === 'string' && rawDetails.trim().length > 0
                            ? rawDetails
                            : undefined;
                    this.dialogService.onOpenErrorDialog({
                        message: errorMessage,
                        branchName,
                        details,
                    });
                }
                return throwError(error);
            })
        );
    }
}
